import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../lib/api";
import TopBar from "../../components/TopBar";
import MatrixBackground from "../../components/MatrixBackground";
import RiderMap from "../../components/RiderMap";
import { NotifyButton } from "../../components/NotifyButton";
import { useBrowserNotifications, useNewItemsNotifier } from "../../hooks/useBrowserNotifications";
import { useRiderGps } from "../../hooks/useRiderGps";
import { playChime, unlockChime } from "../../lib/chime";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { MapPin, Navigation, Package, Phone, Bike, CheckCircle2, LocateFixed } from "lucide-react";

const ASSIGNED_CFG = {
  title: "New delivery assigned to you",
  buildBody: (o) => `Pickup at ${o.shop_name} · ₹${o.total.toFixed(0)}`,
  tag: "rider-assigned",
};

const STATUS_LABEL = {
  confirmed: "Shop is preparing",
  ready: "Ready for pickup",
  picked_up: "Picked up",
  on_the_way: "Out for delivery",
  delivered: "Delivered",
};
const NEXT = { ready: "picked_up", picked_up: "on_the_way", on_the_way: "delivered" };
const NEXT_LABEL = { picked_up: "Mark Picked Up", on_the_way: "Out for Delivery", delivered: "Mark Delivered" };
const GPS_TEXT = { idle: "GPS off", tracking: "Sharing live location", denied: "Location blocked. Allow it in browser settings", error: "Finding your location…", unsupported: "GPS not supported" };

export default function RiderDashboard() {
  const [assigned, setAssigned] = useState([]);
  const [open, setOpen] = useState([]);
  const [history, setHistory] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const claimed = useRef(new Set());
  const notifyItems = useMemo(() => (loaded ? assigned.filter((o) => !claimed.current.has(o.order_id)) : null), [loaded, assigned]);
  const { request, notify } = useBrowserNotifications();
  const notifyWithChime = useCallback((t, b, tag) => { playChime(); toast.info(t, { description: b }); notify(t, b, tag); }, [notify]);
  useNewItemsNotifier(notifyItems, ASSIGNED_CFG, notifyWithChime);
  const gps = useRiderGps(assigned.length > 0);

  const load = useCallback(async () => {
    try {
      const [a, m] = await Promise.all([api.get("/rider/available"), api.get("/orders/mine")]);
      setAssigned(a.data.assigned);
      setOpen(a.data.open);
      setHistory(m.data.filter((o) => o.status === "delivered"));
      setLoaded(true);
    } catch (e) {
      console.error("Rider load failed:", e);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    window.addEventListener("pointerdown", unlockChime);
    return () => { clearInterval(t); window.removeEventListener("pointerdown", unlockChime); };
  }, [load]);

  const update = async (id, status) => {
    try {
      await api.patch(`/orders/${id}/status`, { status });
      toast.success(`Marked ${STATUS_LABEL[status] || status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const claim = async (id) => {
    try {
      claimed.current.add(id);
      await api.post(`/rider/orders/${id}/claim`);
      toast.success("Delivery accepted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not accept"); load(); }
  };

  const today = useMemo(() => new Date().toDateString(), []);
  const doneToday = history.filter((o) => new Date(o.updated_at).toDateString() === today).length;

  return (
    <div className="min-h-screen grid-bg isolate pb-20" data-testid="rider-dashboard">
      <MatrixBackground opacity={0.2} />
      <TopBar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8">
        <div className="glass rounded-3xl p-6 flex items-center justify-between relative overflow-hidden">
          <div>
            <div className="text-xs uppercase tracking-widest text-cyan-300 font-bold">Rider Dashboard</div>
            <h1 className="heading-font text-3xl font-extrabold mt-1">You're online</h1>
            <div className="text-slate-400 text-sm">{assigned.length} active · {doneToday} completed today</div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <NotifyButton request={request} />
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold ${gps === "tracking" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-white/5 border-cyan-500/15 text-slate-400"}`} data-testid="rider-gps-status">
                <LocateFixed size={13} /> {GPS_TEXT[gps]}
              </span>
            </div>
          </div>
          <div className="relative w-16 h-16 rounded-full bg-cyan-400/10 border border-cyan-500/30 inline-flex items-center justify-center shadow-[0_0_30px_rgba(0,240,255,0.25)]">
            <Bike size={28} className="text-cyan-300" />
          </div>
        </div>

        <h2 className="heading-font text-xl font-bold mt-8">My deliveries</h2>
        <div className="mt-3 space-y-3">
          {assigned.length === 0 && (
            <div className="glass rounded-2xl p-10 text-center text-slate-400" data-testid="rider-empty">
              <Package size={28} className="mx-auto text-cyan-300" />
              <div className="mt-3 heading-font font-bold text-slate-200">No deliveries assigned</div>
              <div className="text-sm">New jobs appear here automatically, or accept one from the open list below.</div>
            </div>
          )}
          {assigned.map((o) => <DeliveryCard key={o.order_id} o={o} onUpdate={update} />)}
        </div>

        <h2 className="heading-font text-xl font-bold mt-8">Open orders</h2>
        <div className="mt-3 space-y-3" data-testid="rider-open-list">
          {open.length === 0 && <div className="glass rounded-2xl p-6 text-center text-slate-400 text-sm">No unassigned orders right now.</div>}
          {open.map((o) => (
            <div key={o.order_id} className="glass rounded-2xl p-4 flex items-center justify-between gap-3" data-testid={`rider-open-${o.order_id}`}>
              <div className="min-w-0">
                <div className="font-bold">{o.shop_name} · ₹{o.total.toFixed(0)}</div>
                <div className="text-xs text-slate-400 truncate">{o.shop_address} → {o.address}</div>
              </div>
              <Button className="btn-neon rounded-full shrink-0" onClick={() => claim(o.order_id)} data-testid={`rider-claim-btn-${o.order_id}`}>Accept</Button>
            </div>
          ))}
        </div>

        {history.length > 0 && (
          <>
            <h2 className="heading-font text-sm font-bold mt-8 uppercase tracking-widest text-slate-400">Completed</h2>
            <div className="mt-3 space-y-2">
              {history.slice(0, 10).map((o) => (
                <div key={o.order_id} className="glass rounded-xl p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 size={16} className="text-emerald-400" />
                    <span className="text-sm font-semibold">#{o.order_id.slice(-6).toUpperCase()}</span>
                    <span className="text-xs text-slate-500">{o.customer_name}</span>
                  </div>
                  <div className="text-sm font-bold">₹{o.total.toFixed(2)}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DeliveryCard({ o, onUpdate }) {
  const next = NEXT[o.status];
  return (
    <div className="glass rounded-2xl p-4" data-testid={`rider-order-${o.order_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-cyan-300 font-bold">#{o.order_id.slice(-6).toUpperCase()}</div>
          <div className="heading-font font-bold text-lg">₹{o.total.toFixed(2)} · {o.items.length} item{o.items.length > 1 ? "s" : ""} · {o.payment_method === "cod" ? "Collect cash" : "Prepaid"}</div>
          <div className="text-xs text-slate-400" data-testid={`rider-order-status-${o.order_id}`}>{STATUS_LABEL[o.status]}</div>
        </div>
        <div className="inline-flex items-center gap-1 text-xs text-cyan-300 bg-cyan-500/10 border border-cyan-500/25 rounded-full px-2 py-1 font-bold">
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" /> Live
        </div>
      </div>
      <div className="mt-3 space-y-2 text-sm">
        <Line icon={MapPin} tone="emerald" label="Pickup" value={`${o.shop_name} · ${o.shop_address}`} />
        <Line icon={Navigation} tone="cyan" label="Drop" value={`${o.customer_name} · ${o.address}`} />
        {o.customer_phone && (
          <a href={`tel:${o.customer_phone}`} className="inline-flex items-center gap-2 text-cyan-300 font-semibold text-sm" data-testid={`rider-call-${o.order_id}`}>
            <Phone size={14} /> Call {o.customer_phone}
          </a>
        )}
      </div>
      <RiderMap order={o} />
      {next ? (
        <Button className="btn-neon w-full rounded-full h-11 mt-4" onClick={() => onUpdate(o.order_id, next)} data-testid={`rider-update-status-btn-${o.order_id}`}>
          {NEXT_LABEL[next]}
        </Button>
      ) : (
        <div className="mt-4 text-center text-xs text-slate-400">Head to the shop. You can pick up once it's marked ready.</div>
      )}
    </div>
  );
}

function Line({ icon: Icon, label, value, tone }) {
  const toneBg = tone === "emerald" ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-300" : "bg-cyan-500/15 border-cyan-500/30 text-cyan-300";
  return (
    <div className="flex items-start gap-3">
      <div className={`shrink-0 w-7 h-7 rounded-full border inline-flex items-center justify-center ${toneBg}`}>
        <Icon size={13} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
        <div className="text-sm text-slate-200 leading-snug">{value}</div>
      </div>
    </div>
  );
}
